import { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FieldOption } from '../types/map';

interface CustomComboBoxProps {
  fieldName: string;
  value: string;
  onChange: (value: string, colorValue?: string) => void;
  placeholder?: string;
  disabled?: boolean;
  allowCustom?: boolean;
}

export function CustomComboBox({
  fieldName,
  value,
  onChange,
  placeholder = 'Select or type...',
  disabled = false,
  allowCustom = true,
}: CustomComboBoxProps) {
  const [options, setOptions] = useState<FieldOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');

  const loadOptions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/admin/machines/field-options/${fieldName}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setOptions(data);
      }
    } catch (error) {
      console.error('Failed to load options:', error);
    } finally {
      setLoading(false);
    }
  }, [fieldName]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  async function addCustomOption() {
    if (!customValue.trim()) return;
    
    try {
      const response = await fetch('/api/admin/machines/field-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          fieldName,
          value: customValue.trim(),
        }),
      });

      if (response.ok) {
        await loadOptions();
        onChange(customValue.trim());
        setShowCustomInput(false);
        setCustomValue('');
      }
    } catch (error) {
      console.error('Failed to add custom option:', error);
    }
  }

  const handleValueChange = (selectedValue: string) => {
    if (selectedValue === '__add_custom__') {
      setShowCustomInput(true);
      return;
    }
    
    const option = options.find(opt => opt.value === selectedValue);
    onChange(selectedValue, option?.colorValue || undefined);
  };

  if (showCustomInput) {
    return (
      <div className="flex gap-2">
        <Input
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          placeholder={`Enter custom ${fieldName}...`}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustomOption();
            } else if (e.key === 'Escape') {
              setShowCustomInput(false);
              setCustomValue('');
            }
          }}
          autoFocus
        />
        <Button type="button" onClick={addCustomOption} size="sm">
          <Plus className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setShowCustomInput(false);
            setCustomValue('');
          }}
          size="sm"
        >
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <Select value={value} onValueChange={handleValueChange} disabled={disabled || loading}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="z-[2000]">
        {options.map((option) => (
          <SelectItem key={option.id} value={option.value}>
            {option.value}
          </SelectItem>
        ))}
        {allowCustom && (
          <SelectItem value="__add_custom__" className="text-blue-600">
            Add custom option...
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}
